import type { DimensionUnit } from './dimensions';
import type { FreightQuote } from './freight-api';
import type { FreightWeightUnit, FreightAccessorials as FreightAccessorialsResponse, FreightNotification } from './freight-responses';
import type { FreightPackagingType, FreightClass } from './ltl';

export type InsuranceCommodityCategory =
  | 'GeneralMerchandise'
  | 'Equipment'
  | 'FoodBeverages'
  | 'ChemicalsPaint'
  | 'Electronics'
  | 'Construction'
  | 'Furniture'
  | 'Stonework'
  | 'FragileItems'
  | 'Perishables'
  | 'Jewelry'
  | 'MotorizedTransport'
  | 'ArtAntiques'
  | 'Explosives'
  | 'LifeForms'
  | 'ControlledItems'
  | 'PharmaNonRefrigerated'
  | 'PharmaRefrigerated';

export const InsuranceItemCondition = {
  New: 'New',
  Used: 'Used',
} as const;
export type InsuranceItemCondition = (typeof InsuranceItemCondition)[keyof typeof InsuranceItemCondition];

export type FreightRequestDimensionUnit = 'Unknown' | DimensionUnit;

export type FreightRequestAddress = {
  name?: string | null;
  company?: string | null;
  phone?: string | null;
  line1?: string | null;
  line2?: string | null;
  line3?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  countryCode?: string | null;
  residentialIndicator?: 'Unknown' | 'Residential' | 'Commercial';
  verificationStatus?: string | null;
  verificationMessage?: string | null;
  verificationUtc?: string | null;
  lockAddress?: boolean | null;
  locationType?: string | null;
};

export type FreightRequestHazmatInfo = {
  hazIdType?: 'UN' | 'NA' | null;
  identificationNumber?: string | null;
  properShippingName?: string | null;
  hazardClass?: string[] | null;
  packingGroup?: 'PGI' | 'PGII' | 'PGIII' | 'NoPackagingGroup' | null;
  emergencyPhone?: string | null;
  emergencyContactName?: string | null;
  emergencyResponseReference?: string | null;
  flashpointTemperatureF?: number | null;
  additionalDetails?: string | null;
};

export type FreightRequestCommodity = {
  commodityId?: number | null;
  freightClass?: FreightClass | null;
  description?: string | null;
  packageType?: string | null;
  quantity?: number | null;
  weight: number;
  weightUnit: FreightWeightUnit;
  nmfcCode?: string | null;
  isHazmat: boolean;
  hazmatInfo?: FreightRequestHazmatInfo | null;
};

export type FreightRequestHandlingUnit = {
  handlingUnitId?: number | null;
  packagingType?: FreightPackagingType | null;
  quantity: number;
  length: number;
  width: number;
  height: number;
  dimensionUnit: FreightRequestDimensionUnit;
  weightUnit?: FreightWeightUnit | null;
  isStackable?: boolean;
  freightClass?: FreightClass | null;
  commodities: FreightRequestCommodity[];
};

export type FreightRequestAccessorials = FreightAccessorialsResponse;

export type FreightInsurance = {
  requestInsurance: boolean;
  declaredValue: number;
  commodityCategory?: InsuranceCommodityCategory | null;
  itemCondition?: InsuranceItemCondition | null;
  marksNumbers?: string | null;
};

export type FreightHandlingCharge = {
  value: number;
  unit?: string | null;
};

export type FreightReference = {
  type?: string | null;
  value?: string | null;
};

export type FreightShipmentDetails = {
  shipFromId: number;
  destinationAddress: FreightRequestAddress;
  handlingUnits: FreightRequestHandlingUnit[];
  shipmentDate: string;
  additionalServices?: FreightRequestAccessorials | null;
  insurance?: FreightInsurance | null;
  handlingCharge?: FreightHandlingCharge | null;
};

export type FreightQuoteRequest = {
  // Omit/null to let the backend derive house-vs-negotiated pricing from account state.
  sellerFreightProviderId?: number | null;
  shipment: FreightShipmentDetails;
};

export type FreightBookingDetails = {
  originAddress?: FreightRequestAddress | null;
  destinationAddress?: FreightRequestAddress | null;
  references?: FreightReference[] | null;
  pickupInstructions?: string | null;
  deliveryInstructions?: string | null;
  handlingInstructions?: string | null;
  handlingCharge?: FreightHandlingCharge | null;
  notifications?: FreightNotification[] | null;
};

export type BookFreightFulfillmentPlanRequest = {
  fulfillmentPlanId: string;
  productTransactionId?: string | null;
  bookingDetails?: FreightBookingDetails | null;
  pickupDetails?: FreightPickupDetails | null;
  overrideInventoryWarning?: boolean;
};

export type FreightPickupDetails = {
  pickupDate?: string | null;
  readyTime?: string | null;
  closeTime?: string | null;
  isSelfScheduled: boolean;
  locationType?: string | null;
};

export type FreightTrackShipmentRequest = {
  freightShipmentIds: number[];
};

export type BulkPrintFreightDocumentsRequest = {
  freightShipmentIds: number[];
  documentType: string;
};

export type CancelFreightFulfillmentPlanRequest = {
  fulfillmentPlanIds: string[];
};

export type VoidFreightRequest = {
  fulfillmentPlanIds: string[];
  shipmentIds?: string[];
};

export type FreightCalculateDensityRequest = {
  sellerFreightProviderId: number | null;
  handlingUnits: FreightRequestHandlingUnit[];
};

// 6 is intentionally unused server-side.
export const FreightShipmentSortColumn = {
  CreateDateUtc: 0,
  ShipDateUtc: 1,
  CarrierName: 2,
  Status: 3,
  TotalCharges: 4,
  DestinationCity: 5,
  BolNumber: 7,
  ProNumber: 8,
  EstimatedDeliveryDateUtc: 9,
  OriginCity: 10,
  OrderNumber: 11,
  FreightNumber: 12,
  TotalWeight: 13,
  CancelledDateUtc: 14,
} as const;
export type FreightShipmentSortColumn = (typeof FreightShipmentSortColumn)[keyof typeof FreightShipmentSortColumn];

export const OrderByDirection = {
  Ascending: 0,
  Descending: 1,
} as const;
export type OrderByDirection = (typeof OrderByDirection)[keyof typeof OrderByDirection];

export const FreightStringColumn = {
  BolNumber: 0,
  ProNumber: 1,
  CarrierName: 2,
  CarrierScac: 3,
  DestinationCity: 4,
  DestinationState: 5,
  OriginCity: 6,
  OrderNumber: 7,
} as const;
export type FreightStringColumn = (typeof FreightStringColumn)[keyof typeof FreightStringColumn];

export const FreightCollectionColumn = {
  Status: 0,
  FreightProviderId: 1,
  ShipmentStatus: 2,
  ShipFromId: 3,
} as const;
export type FreightCollectionColumn = (typeof FreightCollectionColumn)[keyof typeof FreightCollectionColumn];

export const FreightDateTimeColumn = {
  CreateDate: 0,
  ShipDate: 1,
  EstimatedDeliveryDate: 2,
  CancelledDate: 3,
} as const;
export type FreightDateTimeColumn = (typeof FreightDateTimeColumn)[keyof typeof FreightDateTimeColumn];

export const FreightNumericColumn = {
  TotalCharges: 0,
  TotalWeight: 1,
  TransitDays: 2,
} as const;
export type FreightNumericColumn = (typeof FreightNumericColumn)[keyof typeof FreightNumericColumn];

export const StringOperatorEnum = {
  Empty: 0,
  NotEmpty: 1,
  Equals: 2,
  NotEqual: 3,
  BeginsWith: 4,
  DoesNotBeginWith: 5,
  EndsWith: 6,
  DoesNotEndWith: 7,
  Contains: 8,
  DoesNotContain: 9,
} as const;
export type StringOperatorEnum = (typeof StringOperatorEnum)[keyof typeof StringOperatorEnum];

export const NumericOperatorEnum = {
  Eq: 0,
  Ne: 1,
  Gt: 2,
  Gte: 3,
  Lt: 4,
  Lte: 5,
} as const;
export type NumericOperatorEnum = (typeof NumericOperatorEnum)[keyof typeof NumericOperatorEnum];

export type FreightStringFilter = {
  column: FreightStringColumn;
  operator: StringOperatorEnum;
  values?: string[] | null;
};

export type FreightCollectionFilter = {
  column: FreightCollectionColumn;
  values?: string[] | null;
  isIn?: boolean;
};

export type FreightDateTimeFilter = {
  column: FreightDateTimeColumn;
  operator: NumericOperatorEnum;
  value?: string | null;
};

export type FreightNumericFilter = {
  column: FreightNumericColumn;
  operator: NumericOperatorEnum;
  values?: string[] | null;
};

export type FreightShipmentGridFilter = {
  stringFilters?: FreightStringFilter[] | null;
  collectionFilters?: FreightCollectionFilter[] | null;
  dateTimeFilters?: FreightDateTimeFilter[] | null;
  numericFilters?: FreightNumericFilter[] | null;
};

export type PageData = {
  pageNumber: number;
  pageSize: number;
};

export type FreightFulfillmentPlanGridRequest = {
  searchTerm?: string | null;
  filter?: FreightShipmentGridFilter | null;
  orderBy?: FreightShipmentSortColumn | null;
  orderByDirection?: OrderByDirection | null;
  page?: PageData | null;
};

/**
 * Request for `api/FreightShipmentGrid`, the shipment-keyed sibling of the fulfillment-plan grid.
 * Same shape, except `page` is required — the endpoint answers 400 without it.
 */
export type FreightShipmentGridRequest = Omit<FreightFulfillmentPlanGridRequest, 'page'> & {
  page: PageData;
};

/**
 * Request for `api/FreightGridStatusCounts`.
 * Counts exist solely for an active quicksearch — quicksearch and filter-bar filters are
 * mutually exclusive (the filter bar is hidden while a search is active).
 */
export type FreightGridStatusCountsRequest = {
  searchTerm: string;
};

export const CommodityPresetSortColumn = {
  Name: 0,
  FreightClassId: 1,
  Weight: 2,
  Quantity: 3,
  IsHazmat: 4,
  CreatedUtc: 5,
  ModifiedUtc: 6,
} as const;
export type CommodityPresetSortColumn = (typeof CommodityPresetSortColumn)[keyof typeof CommodityPresetSortColumn];

export type FreightCommodityPresetGridFilter = {
  stringFilters?: FreightStringFilter[] | null;
  collectionFilters?: FreightCollectionFilter[] | null;
  dateTimeFilters?: FreightDateTimeFilter[] | null;
  numericFilters?: FreightNumericFilter[] | null;
};

export type FreightCommodityPresetGridRequest = {
  searchTerm?: string | null;
  filter?: FreightCommodityPresetGridFilter | null;
  orderBy?: CommodityPresetSortColumn | null;
  orderByDirection?: OrderByDirection | null;
  page?: PageData | null;
};

export const HandlingUnitPresetSortColumn = {
  Name: 0,
  FreightPackageTypeId: 1,
  Weight: 2,
  Quantity: 3,
  CreatedUtc: 4,
  ModifiedUtc: 5,
} as const;
export type HandlingUnitPresetSortColumn = (typeof HandlingUnitPresetSortColumn)[keyof typeof HandlingUnitPresetSortColumn];

export type FreightHandlingUnitPresetGridFilter = {
  stringFilters?: FreightStringFilter[] | null;
  dateTimeFilters?: FreightDateTimeFilter[] | null;
  numericFilters?: FreightNumericFilter[] | null;
};

export type FreightHandlingUnitPresetGridRequest = {
  searchTerm?: string | null;
  filter?: FreightHandlingUnitPresetGridFilter | null;
  orderBy?: HandlingUnitPresetSortColumn | null;
  orderByDirection?: OrderByDirection | null;
  page?: PageData | null;
};

export type FreightFulfillmentPlanExportRequest = {
  fileType: 'CSV';
  fulfillmentPlanIds?: string[] | null;
  searchTerm?: string | null;
  filter?: FreightShipmentGridFilter | null;
  orderBy?: FreightShipmentSortColumn | null;
  orderByDirection?: OrderByDirection | null;
  // Defaults to false server-side (line-item detail, today's only behavior) if omitted.
  shipmentLevelOnly?: boolean;
};

export type CustomExtractExportFreightRequest = {
  fileType: 'CSV';
  extractId: string;
  fulfillmentPlanIds?: string[] | null;
  searchTerm?: string | null;
  filter?: FreightShipmentGridFilter | null;
  orderBy?: FreightShipmentSortColumn | null;
  orderByDirection?: OrderByDirection | null;
};

export type SaveFreightConfigurationRequest = {
  email: string | null;
  freightFulfillmentPlanStatusID: number | null;
  freightNumber: string;
  sellerFreightProviderID?: number | null;
  offerId?: string | null;
  carrierName?: string | null;
  carrierScac?: string | null;
  serviceName?: string | null;
  totalCharges?: number | null;
  totalChargesCurrencyCode?: string | null;
  transitDays?: number | null;
  estimatedDeliveryDateUtc?: string | null;
  pickupDateUtc?: string | null;
  quoteExpirationDateUtc?: string | null;
  totalWeight?: number | null;
  totalWeightUnitOfMeasureID?: number | null;
  shipTo?: FreightRequestAddress | null;
  handlingUnits?: FreightRequestHandlingUnit[] | null;
  additionalServices?: FreightRequestAccessorials | null;
  insurance?: FreightInsurance | null;
  pickupDetails?: FreightPickupDetails | null;
  shipFromId?: string | null;
  shipDate?: string | null;
  allQuotes?: FreightQuote[] | null;
  pickupInstructions?: string | null;
  deliveryInstructions?: string | null;
  handlingInstructions?: string | null;
  references?: FreightReference[] | null;
  handlingCharge?: FreightHandlingCharge | null;
  notifications?: FreightNotification[] | null;
};

export type FreightFulfillmentPlanSaveQuoteRequest = {
  email?: string | null;
  fulfillmentPlanId?: string | null;
  sellerFreightProviderId?: number;
  productTransactionId?: string | null;
  freightNumber?: string | null;
  offerId?: string | null;
  carrierName?: string | null;
  carrierScac?: string | null;
  serviceName?: string | null;
  totalCharges?: number | null;
  transitDays?: number | null;
  estimatedDeliveryDate?: string | null;
  totalWeight?: number | null;
  shipDate?: string | null;
  quoteExpirationDate?: string | null;
  shipFromWarehouseId?: number | null;
  destinationAddress?: FreightRequestAddress | null;
  handlingUnits?: FreightRequestHandlingUnit[] | null;
  additionalServices?: FreightRequestAccessorials | null;
  insurance?: FreightInsurance | null;
  pickupDetails?: FreightPickupDetails | null;
  allQuotes?: FreightQuote[] | null;
  pickupInstructions?: string | null;
  deliveryInstructions?: string | null;
  handlingInstructions?: string | null;
  notifications?: FreightNotification[] | null;
};
